import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import ModelComparisonUnit from './ModelComparisonUnit'
import { type Focus, focusOf } from './transcriptDiff'

import styles from './ModelComparison.module.scss'

import { isApiError } from '@/api'
import {
  getMeetingTurnPreference,
  saveMeetingTurnPreference,
  type TurnPreferenceChoice,
  type TurnPreferenceReason,
  type TurnPreferenceResponse,
  type TurnPreferenceVote,
} from '@/api/internal'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import { READ_DEFAULTS } from '@/hooks/useAttributionGt'
import { type SpeakerIdentity } from '@/pages/app/AttributionViewer/artifactDerivations'
import { buildSpeakerStyleMap } from '@/pages/app/AttributionViewer/components/colors'
import { attributionViewerKeys } from '@/pages/app/AttributionViewer/queryKeys'

type ModelComparisonProps = {
  meetingId: string
  query: UseQueryResult<TurnPreferenceResponse>
  playClip: (startMs: number, endMs: number) => void
  pause: () => void
  isPlaying: boolean
  audioTime: number | null
  playDisabled: boolean
}

const UNAVAILABLE_MESSAGES: Record<TurnPreferenceReason, string> = {
  no_displayed_job: 'This meeting has no refined transcript to compare yet.',
  revision_changed: 'The transcript changed while loading. Refresh to try again.',
  capture_unavailable: 'The captured audio for this meeting was not retained.',
  capture_too_large: 'The captured data for this meeting is too large to compare.',
  evaluation_unavailable: "The second transcript for this meeting isn't ready yet. Check back later.",
  timing_unavailable: 'The transcripts have no word timings to align on.',
  words_unmapped: 'The transcripts could not be aligned word by word.',
  insufficient_units: 'The two transcripts agree too closely to compare.',
  before_round_start: 'This meeting was recorded before the current comparison round began.',
}
const GENERIC_UNAVAILABLE = 'No comparison is available for this meeting.'

const SECTIONS: { focus: Focus; title: string; hint: string }[] = [
  {
    focus: 'speakers',
    title: 'Part 1 · Who said it',
    hint: 'Judge only the speaker labels. The words may differ; ignore that. Pick the side that gives each line to the right person.',
  },
  {
    focus: 'words',
    title: 'Part 2 · What was said',
    hint: 'Judge only the words. Speaker labels are hidden on purpose. Pick the side whose words match the audio more closely.',
  },
]

// The first read builds and stores the session's units, so only mount this on the user's explicit navigation.
export const useTurnPreferenceQuery = (meetingId: string): UseQueryResult<TurnPreferenceResponse> =>
  useQuery({
    ...READ_DEFAULTS,
    enabled: Boolean(meetingId),
    queryKey: attributionViewerKeys.turnPreference(meetingId),
    queryFn: ({ signal }) => getMeetingTurnPreference(meetingId, signal).then((response) => response.data),
  })

const ModelComparison = ({
  meetingId,
  query,
  playClip,
  pause,
  isPlaying,
  audioTime,
  playDisabled,
}: ModelComparisonProps) => {
  const queryClient = useQueryClient()
  const queryKey = attributionViewerKeys.turnPreference(meetingId)
  const [picks, setPicks] = useState<Record<string, TurnPreferenceChoice>>({})
  const [confirming, setConfirming] = useState(false)
  // Clips can overlap in time, so the playhead alone cannot say which one is playing.
  const [activeUnitId, setActiveUnitId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const noticeRef = useRef<HTMLDivElement>(null)
  const footRef = useRef<HTMLDivElement>(null)

  const save = useMutation({
    retry: 0,
    mutationFn: (votes: TurnPreferenceVote[]) => saveMeetingTurnPreference(meetingId, votes),
    onSuccess: (response) => {
      setSaveError(null)
      queryClient.setQueryData<TurnPreferenceResponse>(queryKey, response.data)
      footRef.current?.focus()
    },
    onError: (error) => {
      if (isApiError(error) && error.status === 409) {
        setSaveError('These choices were already submitted.')
        void queryClient.invalidateQueries({ queryKey })
      } else setSaveError("Couldn't save your choices. Try again.")
    },
  })

  const data = query.data
  const units = useMemo(() => data?.units ?? [], [data])
  const finished = data?.status === 'finished'
  const readOnly = finished
  const recorded = useMemo(() => {
    const map: Record<string, TurnPreferenceChoice> = {}
    for (const vote of data?.votes ?? []) map[vote.unit_id] = vote.choice
    return map
  }, [data])
  const shownPicks = finished ? recorded : picks
  const total = units.length
  const chosen = units.filter((unit) => shownPicks[unit.unit_id] !== undefined).length

  const speakerStyles = useMemo(() => {
    const identities = new Map<string, SpeakerIdentity>()
    for (const unit of units) {
      for (const turn of [...unit.sides.a.turns, ...unit.sides.b.turns]) {
        if (identities.has(turn.name)) continue
        identities.set(turn.name, {
          key: turn.name,
          colorKey: turn.name,
          label: turn.name,
          name: turn.is_placeholder ? null : turn.name,
        })
      }
    }
    return buildSpeakerStyleMap(identities.values())
  }, [units])

  const togglePick = (unitId: string, choice: TurnPreferenceChoice) =>
    setPicks((current) => {
      const next = { ...current }
      if (next[unitId] === choice) delete next[unitId]
      else next[unitId] = choice
      return next
    })

  const submit = () => {
    const votes = units.flatMap((unit) => {
      const choice = picks[unit.unit_id]
      return choice ? [{ unit_id: unit.unit_id, choice }] : []
    })
    setConfirming(false)
    save.mutate(votes)
  }

  const retry = () => {
    // The notice stays mounted across the reload, so focus survives the button it replaces.
    noticeRef.current?.focus()
    void query.refetch()
  }

  const renderNotice = () => {
    if (query.isPending) return 'Preparing the comparison… The first open can take up to a minute.'
    if (query.isError || !data)
      return (
        <>
          <b>{"Couldn't load the comparison."}</b>
          <Button title="Try again" type="light" onClick={retry} />
        </>
      )
    if (data.status === 'unavailable' || units.length === 0)
      return (
        <>
          <b>No model comparison for this meeting.</b>
          {(data.reason && UNAVAILABLE_MESSAGES[data.reason]) ?? GENERIC_UNAVAILABLE}
        </>
      )
    return null
  }

  const notice = renderNotice()
  if (notice !== null)
    return (
      <div className={styles.root}>
        <div className={styles.notice} ref={noticeRef} tabIndex={-1} role="status">
          {notice}
        </div>
      </div>
    )

  return (
    <div className={styles.root}>
      {!readOnly && (
        <p className={styles.intro}>
          Each clip plays the same stretch of the meeting as two models transcribed it. The sides are shuffled and
          unlabeled. Press Play to hear the clip, then click the side you prefer.
        </p>
      )}
      {SECTIONS.map((section) => {
        const sectionUnits = units.filter((unit) => focusOf(unit) === section.focus)
        if (sectionUnits.length === 0) return null
        return (
          <section key={section.focus} className={styles.section} aria-labelledby={`turn-pref-${section.focus}`}>
            <div className={styles.sectionHead}>
              <h3 id={`turn-pref-${section.focus}`} className={styles.sectionTitle}>
                {section.title}
              </h3>
              <p className={styles.sectionHint}>{section.hint}</p>
            </div>
            <div className={styles.units}>
              {sectionUnits.map((unit) => {
                const index = units.indexOf(unit)
                const inRange = audioTime != null && audioTime >= unit.start_ms && audioTime <= unit.end_ms
                const playing = isPlaying && inRange && activeUnitId === unit.unit_id
                return (
                  <ModelComparisonUnit
                    key={unit.unit_id}
                    unit={unit}
                    index={index}
                    pick={shownPicks[unit.unit_id]}
                    readOnly={readOnly}
                    speakerStyles={speakerStyles}
                    playing={playing}
                    playDisabled={playDisabled}
                    progress={
                      audioTime == null ? 0 : (audioTime - unit.start_ms) / Math.max(1, unit.end_ms - unit.start_ms)
                    }
                    onPick={(choice) => togglePick(unit.unit_id, choice)}
                    onTogglePlay={() => {
                      if (playing) return pause()
                      setActiveUnitId(unit.unit_id)
                      playClip(unit.start_ms, unit.end_ms)
                    }}
                  />
                )
              })}
            </div>
          </section>
        )
      })}
      <div className={styles.foot} ref={footRef} tabIndex={-1} role="status">
        <span className={styles.progress}>
          {readOnly ? 'Submitted · ' : ''}
          {chosen} of {total} chosen
        </span>
        <span className={styles.footHint}>
          {readOnly
            ? 'This comparison is read-only now.'
            : chosen === total
              ? 'All clips chosen.'
              : 'Clips left unchosen are saved as no preference.'}
        </span>
        {saveError && (
          <span className={styles.error} role="alert">
            {saveError}
          </span>
        )}
        {!readOnly && (
          <div className={styles.submit}>
            <Button
              title="Submit choices"
              type="dark"
              isLoading={save.isPending}
              isDisabled={save.isPending}
              onClick={() => setConfirming(true)}
            />
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={confirming}
        title={chosen === total ? `Submit ${total} choices?` : `Submit ${chosen} of ${total} choices?`}
        message="You can't change them afterwards."
        confirmLabel="Submit"
        confirmType="dark"
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
      />
    </div>
  )
}

export default ModelComparison
